import { jsPDF } from "jspdf";
import { formatMoney } from "./formatMoney";

export interface MetricWorkflowStep {
  name: string;
  documentFormula?: string;
}

export interface ContractMetricInput {
  contractNumber: string;
  signedDate: string;
  parties: string[];
  subject: string;
  netAmount: number;
  vatAmount: number;
  grossAmount: number;
  duration: string;
  noticePeriod: string;
  /** Etapy procedury akceptacji (z workflow dla typu „contract"). */
  workflowSteps?: MetricWorkflowStep[];
}

const fmtDate = (iso: string) =>
  iso
    ? new Date(iso).toLocaleDateString("pl-PL", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "—";

let fontRegularB64: string | null = null;
let fontBoldB64: string | null = null;

async function loadFontB64(url: string): Promise<string> {
  const res = await fetch(url);
  const buf = await res.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = "";
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export async function generateContractMetricPdf(
  data: ContractMetricInput,
): Promise<Blob> {
  if (!fontRegularB64)
    fontRegularB64 = await loadFontB64("/fonts/LiberationSans-Regular.ttf");
  if (!fontBoldB64)
    fontBoldB64 = await loadFontB64("/fonts/LiberationSans-Bold.ttf");

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  doc.addFileToVFS("LiberationSans-Regular.ttf", fontRegularB64);
  doc.addFont("LiberationSans-Regular.ttf", "LiberationSans", "normal");
  doc.addFileToVFS("LiberationSans-Bold.ttf", fontBoldB64);
  doc.addFont("LiberationSans-Bold.ttf", "LiberationSans", "bold");

  const L = 14;
  const R = 196;
  const W = 182;
  let y = 16;

  const setF = (size: number, bold = false, r = 28, g = 27, b = 31) => {
    doc.setFont("LiberationSans", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(r, g, b);
  };

  const hline = () => {
    doc.setLineWidth(0.35);
    doc.setDrawColor(93, 73, 199);
    doc.line(L, y, R, y);
  };

  // ── Nagłówek dokumentu ─────────────────────────────────────────────
  setF(13, true, 93, 73, 199);
  doc.text("METRYKA UMOWY", L, y);
  y += 4;
  hline();
  y += 7;

  // ── Dane umowy ─────────────────────────────────────────────────────
  setF(8, true, 100, 90, 120);
  doc.text("PRZEDMIOT:", L, y);
  setF(9.5, true, 28, 27, 31);
  const subjLines = doc.splitTextToSize(data.subject || "—", W - 26);
  doc.text(subjLines, L + 26, y);
  y += subjLines.length * 5 + 3;

  const col1x = L;
  const col2x = L + 90;
  const labelW = 30;

  const drawMetaRow = (k1: string, v1: string, k2: string, v2: string) => {
    setF(7.5, true, 120, 110, 140);
    doc.text(k1, col1x, y);
    setF(9, false, 28, 27, 31);
    doc.text(v1, col1x + labelW, y);
    setF(7.5, true, 120, 110, 140);
    doc.text(k2, col2x, y);
    setF(9, false, 28, 27, 31);
    doc.text(v2, col2x + labelW, y);
    y += 5.5;
  };

  drawMetaRow("NUMER:", data.contractNumber || "—", "ZAWARTA:", fmtDate(data.signedDate));
  drawMetaRow("NETTO:", formatMoney(data.netAmount), "VAT:", formatMoney(data.vatAmount));
  drawMetaRow("BRUTTO:", formatMoney(data.grossAmount), "WIĄŻE:", data.duration || "—");

  setF(7.5, true, 120, 110, 140);
  doc.text("WYPOWIEDZENIE:", col1x, y);
  setF(9, false, 28, 27, 31);
  doc.text(data.noticePeriod || "—", col1x + 32, y);
  y += 6;

  setF(7.5, true, 120, 110, 140);
  doc.text("STRONY:", col1x, y);
  setF(9, false, 28, 27, 31);
  const partyLines = doc.splitTextToSize(
    data.parties.length ? data.parties.join("; ") : "—",
    W - labelW,
  );
  doc.text(partyLines, col1x + labelW, y);
  y += partyLines.length * 5 + 5;

  hline();
  y += 7;

  // ── Procedura akceptacji ───────────────────────────────────────────
  setF(10, true, 93, 73, 199);
  doc.text("PROCEDURA AKCEPTACJI", L, y);
  y += 7;

  const steps = data.workflowSteps ?? [];
  if (steps.length === 0) {
    setF(9, false, 120, 110, 140);
    doc.text("Brak zdefiniowanej procedury akceptacji dla umów.", L, y);
    y += 6;
  } else {
    steps.forEach((step, i) => {
      if (y > 260) {
        doc.addPage();
        y = 16;
      }
      setF(8, true, 120, 110, 140);
      doc.text(`Etap ${i + 1}`, L, y);
      y += 4.5;

      setF(10, true, 28, 27, 31);
      const nameLines = doc.splitTextToSize(step.name, W - 6);
      doc.text(nameLines, L + 3, y);
      y += nameLines.length * 5 + 1;

      if (step.documentFormula?.trim()) {
        doc.setFillColor(248, 246, 255);
        const formulaLines = doc.splitTextToSize(step.documentFormula, W - 12);
        const blockH = formulaLines.length * 4.5 + 4;
        doc.rect(L + 3, y - 1, W - 3, blockH, "F");
        doc.setLineWidth(0.5);
        doc.setDrawColor(93, 73, 199);
        doc.line(L + 3, y - 1, L + 3, y - 1 + blockH);
        setF(8.5, false, 60, 50, 80);
        doc.text(formulaLines, L + 7, y + 2);
        y += blockH + 5;
      } else {
        y += 3;
      }
    });
  }

  y += 3;
  hline();
  y += 8;

  // ── Stopka ─────────────────────────────────────────────────────────
  setF(7.5, false, 130, 120, 140);
  doc.text(`Data wydruku: ${new Date().toLocaleDateString("pl-PL")}`, L, y);
  doc.text(".......................................", R, y, { align: "right" });
  y += 4.5;
  setF(7, false, 170, 165, 180);
  doc.text("podpis i pieczęć", R, y, { align: "right" });

  return doc.output("blob");
}
